"use client"

import { useEffect, useState } from "react"
import { IconHistory, IconTrophy, IconLoader2 } from "@tabler/icons-react"
import { useTranslations } from "next-intl"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"

import { getMyProgressHistory, type ProgressHistoryResponse } from "@/lib/targets-api"
import { useFormatCurrency } from "@/hooks/use-currency"
import type { AgentProgress } from "@/types"

interface PerformanceHistorySheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function PerformanceHistorySheet({
  open,
  onOpenChange,
}: PerformanceHistorySheetProps) {
  const t = useTranslations("dashboard")
  const [data, setData] = useState<ProgressHistoryResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [allHistory, setAllHistory] = useState<AgentProgress[]>([])

  useEffect(() => {
    if (open) {
      loadHistory(1, true)
    }
  }, [open])

  const loadHistory = async (pageNum: number, reset = false) => {
    setIsLoading(true)
    try {
      const response = await getMyProgressHistory()
      setData(response)
      if (reset) {
        setAllHistory(response.history)
      } else {
        setAllHistory((prev) => [...prev, ...response.history])
      }
      setPage(pageNum)
    } catch (error) {
      console.error("Failed to load history:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const { formatCurrencyNoDecimals } = useFormatCurrency()

  const formatValue = (value: number, metric?: string) => {
    if (metric === "quotes_accepted_revenue") {
      return formatCurrencyNoDecimals(value)
    }
    return value.toString()
  }

  const getProgressColor = (percentage: number) => {
    if (percentage >= 100) return "bg-green-500"
    if (percentage >= 80) return "bg-yellow-500"
    return "bg-blue-500"
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <IconHistory className="h-5 w-5" />
            {t("history.header")}
          </SheetTitle>
          <SheetDescription>
            {t("history.description")}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {isLoading && allHistory.length === 0 ? (
            // Initial loading state
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="border rounded-lg p-4 space-y-3">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-2 w-full" />
                  <div className="flex justify-between">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                </div>
              ))}
            </div>
          ) : allHistory.length === 0 ? (
            // Empty state
            <div className="text-center py-12">
              <IconHistory className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground">
                {t("history.empty")}
              </p>
            </div>
          ) : (
            // History cards
            <>
              {allHistory.map((progress) => {
                const percentage = Math.min(progress.percentage, 100)
                const progressColor = getProgressColor(progress.percentage)

                return (
                  <div
                    key={progress.id}
                    className="border rounded-lg p-4 space-y-3"
                  >
                    {/* Period Header */}
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">
                          {progress.period?.period_label || t("history.period")}
                        </h4>
                        <p className="text-xs text-muted-foreground">
                          {progress.period?.metric_label}
                        </p>
                      </div>
                      {progress.tier_achieved && (
                        <Badge variant="outline" className="text-green-600">
                          <IconTrophy className="h-3 w-3 mr-1" />$
                          {progress.tier_achieved.bonus_amount}
                        </Badge>
                      )}
                    </div>

                    {/* Progress Bar */}
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>{t("history.progress")}</span>
                        <span className="font-medium">
                          {progress.percentage.toFixed(1)}%
                        </span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full ${progressColor} transition-all`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>

                    {/* Values */}
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        {formatValue(
                          progress.current_value,
                          progress.period?.metric
                        )}{" "}
                        {t("history.of")}{" "}
                        {formatValue(
                          progress.target_value,
                          progress.period?.metric
                        )}
                      </span>
                      {progress.bonus_earned && (
                        <span className="font-medium text-green-600">
                          {t("history.earned", { amount: progress.bonus_earned })}
                        </span>
                      )}
                    </div>

                    {/* Activity Stats */}
                    <div className="grid grid-cols-3 gap-2 pt-2 border-t text-center text-xs">
                      <div>
                        <p className="font-semibold">{progress.status_updates_count}</p>
                        <p className="text-muted-foreground">{t("history.statUpdates")}</p>
                      </div>
                      <div>
                        <p className="font-semibold">{progress.comments_count}</p>
                        <p className="text-muted-foreground">{t("history.statComments")}</p>
                      </div>
                      <div>
                        <p className="font-semibold">{progress.quotes_sent_count}</p>
                        <p className="text-muted-foreground">{t("history.statQuotes")}</p>
                      </div>
                    </div>
                  </div>
                )
              })}

              {/* Load More Button */}
              {data && page < data.pagination.last_page && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => loadHistory(page + 1)}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <IconLoader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t("history.loading")}
                    </>
                  ) : (
                    t("history.loadMore")
                  )}
                </Button>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
